/**
 * Composant de filtres avancés pour le module de rapports RH
 * Filtrage par période, département, employé, type de contrat, etc.
 */

import React, { useState, useEffect, useRef } from 'react';
import useBusinessResource from '../../hooks/useBusinessResource';
import BusinessResourceNotice from '../BusinessResourceNotice';
import { getHRReportResource, reportError, useHRReportScope } from './hrReportsApi';
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  DatePicker,
  Switch,
  Button,
  Space,
  Divider,
  Tag,
  Typography,
  Collapse,
  Checkbox,
  Slider,
  Alert
} from 'antd';
import {
  FilterOutlined,
  ClearOutlined,
  SaveOutlined,
  CalendarOutlined,
  TeamOutlined,
  DollarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;
const { Text } = Typography;

interface FilterState {
  period: {
    start: dayjs.Dayjs | null;
    end: dayjs.Dayjs | null;
    preset: string;
  };
  departments: string[];
  employees: string[];
  contractTypes: string[];
  salaryRange: [number, number];
  positions: string[];
  employmentStatus: string[];
  includeTerminated: boolean;
  includeInterns: boolean;
  complianceLevel: string;
}

interface FilterOptions {
  departments: Array<{ id: string; name: string; employeeCount: number }>;
  positions: Array<{ id: string; title: string; departmentId?: string; departmentName?: string; employeeCount: number }>;
  contractTypes: Array<{ value: string; label: string; employeeCount: number }>;
  salaryRange: { min: number; max: number; average: number; totalEmployees: number };
}

interface AdvancedFiltersProps {
  onFiltersChange: (filters: FilterState) => void;
  loading?: boolean;
  active?: boolean;
  onValidityChange?: (valid: boolean) => void;
}

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({ 
  onFiltersChange, 
  loading = false,
  active = true,
  onValidityChange,
}) => {
  const scope = useHRReportScope();
  const [form] = Form.useForm();
  const [filters, setFilters] = useState<FilterState>({
    period: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month'),
      preset: 'current_month'
    },
    departments: [],
    employees: [],
    contractTypes: [],
    salaryRange: [0, 1000000],
    positions: [],
    employmentStatus: ['active'],
    includeTerminated: false,
    includeInterns: true,
    complianceLevel: 'all'
  });

  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [savedFilters, setSavedFilters] = useState<string[]>([]);
  const emptyOptions = useRef<FilterOptions>({
    departments: [],
    positions: [],
    contractTypes: [],
    salaryRange: { min: 0, max: 1000000, average: 0, totalEmployees: 0 }
  });
  const options = useBusinessResource<FilterOptions>({ ...scope, scopeLevel: 'company', resource: 'hr-report-filter-options',
    enabled: scope.allowed, active, load: signal => getHRReportResource('/api/hr/optimized-exports/filter-options', scope.companyId, signal) });
  const filterOptions = options.data || emptyOptions.current;
  const loadingOptions = options.loading;
  const initializedSalary = useRef(false);
  const touchedSalary = useRef(false);
  const missing = [
    ...filters.departments.filter(id => options.data && !filterOptions.departments.some(item => String(item.id) === String(id))).map(id => `département ${id}`),
    ...filters.positions.filter(id => options.data && !filterOptions.positions.some(item => String(item.id) === String(id))).map(id => `poste ${id}`),
    ...filters.contractTypes.filter(id => options.data && !filterOptions.contractTypes.some(item => String(item.value) === String(id))).map(id => `contrat ${id}`),
  ];
  const valid = options.status === 'verified' && missing.length === 0;
  useEffect(() => { onValidityChange?.(valid); }, [valid, onValidityChange]);
  useEffect(() => {
    if (options.data && !initializedSalary.current) {
      initializedSalary.current = true;
      if (!touchedSalary.current) setFilters(previous => ({ ...previous, salaryRange: [options.data!.salaryRange.min, options.data!.salaryRange.max] }));
    }
  }, [options.data]);

  const getCurrentQuarter = () => {
    const month = dayjs().month();
    const quarterStart = Math.floor(month / 3) * 3;
    return {
      start: dayjs().month(quarterStart).startOf('month'),
      end: dayjs().month(quarterStart + 2).endOf('month')
    };
  };

  const getLastQuarter = () => {
    const month = dayjs().month();
    const currentQuarterStart = Math.floor(month / 3) * 3;
    const lastQuarterStart = currentQuarterStart - 3;
    
    if (lastQuarterStart < 0) {
      // Dernier trimestre de l'année précédente
      return {
        start: dayjs().subtract(1, 'year').month(9).startOf('month'),
        end: dayjs().subtract(1, 'year').month(11).endOf('month')
      };
    }
    
    return {
      start: dayjs().month(lastQuarterStart).startOf('month'),
      end: dayjs().month(lastQuarterStart + 2).endOf('month')
    };
  };

  const presetPeriods = [
    { key: 'current_month', label: 'Mois actuel', start: dayjs().startOf('month'), end: dayjs().endOf('month') },
    { key: 'last_month', label: 'Mois dernier', start: dayjs().subtract(1, 'month').startOf('month'), end: dayjs().subtract(1, 'month').endOf('month') },
    { key: 'current_quarter', label: 'Trimestre actuel', start: getCurrentQuarter().start, end: getCurrentQuarter().end },
    { key: 'last_quarter', label: 'Trimestre dernier', start: getLastQuarter().start, end: getLastQuarter().end },
    { key: 'current_year', label: 'Année actuelle', start: dayjs().startOf('year'), end: dayjs().endOf('year') },
    { key: 'last_year', label: 'Année dernière', start: dayjs().subtract(1, 'year').startOf('year'), end: dayjs().subtract(1, 'year').endOf('year') },
    { key: 'last_6_months', label: '6 derniers mois', start: dayjs().subtract(6, 'months').startOf('month'), end: dayjs().endOf('month') },
    { key: 'custom', label: 'Période personnalisée', start: null, end: null }
  ];

  useEffect(() => {
    // Calculer le nombre de filtres actifs
    let count = 0;
    if (filters.departments.length > 0) count++;
    if (filters.employees.length > 0) count++;
    if (filters.contractTypes.length > 0) count++;
    if (filters.positions.length > 0) count++;
    if (filters.salaryRange[0] > filterOptions.salaryRange.min || filters.salaryRange[1] < filterOptions.salaryRange.max) count++;
    if (!filters.includeTerminated || !filters.includeInterns) count++;
    if (filters.complianceLevel !== 'all') count++;

    setActiveFiltersCount(count);
    onFiltersChange(filters);
  }, [filters, onFiltersChange, filterOptions.salaryRange]);

  const handlePeriodPresetChange = (preset: string) => {
    const selectedPreset = presetPeriods.find(p => p.key === preset);
    if (selectedPreset && selectedPreset.key !== 'custom') {
      setFilters(prev => ({
        ...prev,
        period: {
          start: selectedPreset.start,
          end: selectedPreset.end,
          preset
        }
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        period: {
          ...prev.period,
          preset
        }
      }));
    }
  };

  const handleCustomPeriodChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setFilters(prev => ({
        ...prev,
        period: {
          start: dates[0],
          end: dates[1],
          preset: 'custom'
        }
      }));
    }
  };

  const clearFilters = () => {
    const defaultFilters: FilterState = {
      period: {
        start: dayjs().startOf('month'),
        end: dayjs().endOf('month'),
        preset: 'current_month'
      },
      departments: [],
      employees: [],
      contractTypes: [],
      salaryRange: [filterOptions.salaryRange.min, filterOptions.salaryRange.max],
      positions: [],
      employmentStatus: ['active'],
      includeTerminated: false,
      includeInterns: true,
      complianceLevel: 'all'
    };
    setFilters(defaultFilters);
    form.resetFields();
  };

  const saveCurrentFilters = () => {
    const filterName = `Filtre_${dayjs().format('DD-MM-YYYY_HH-mm')}`;
    setSavedFilters(prev => [...prev, filterName]);
    // Ici vous pourriez sauvegarder en base de données ou localStorage
    localStorage.setItem(`hr_filter_${scope.userId}_${scope.companyId}_${scope.warehouseId || ''}_${filterName}`, JSON.stringify(filters));
  };

  return (
    <Card className="hr-reports-filter-card"
      title={
        <Space>
          <FilterOutlined />
          <span>Filtres Avancés</span>
          {activeFiltersCount > 0 && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''} actif{activeFiltersCount > 1 ? 's' : ''}
            </Tag>
          )}
        </Space>
      }
      extra={
        <Space>
          <Button 
            icon={<SaveOutlined />} 
            onClick={saveCurrentFilters}
            size="small"
          >
            Sauvegarder
          </Button>
          <Button 
            icon={<ClearOutlined />} 
            onClick={clearFilters}
            size="small"
          >
            Réinitialiser
          </Button>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      <BusinessResourceNotice resource={options} label="des options de filtrage" errorMessage={reportError(options.error)} />
      {missing.length > 0 && <Alert type="warning" showIcon message="Une sélection n’est plus disponible"
        description={`${missing.join(', ')}. Votre sélection est conservée. Choisissez une référence disponible avant l’export.`} style={{ marginBottom: 16 }} />}
      
      {!loadingOptions && filterOptions.departments.length > 0 && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 16 }}>
          Options actualisées · {filterOptions.departments.length} département(s) · {filterOptions.positions.length} poste(s) · {filterOptions.contractTypes.length} type(s) de contrat
        </Text>
      )}

      <Form form={form} layout="vertical">
        <Collapse defaultActiveKey={['period', 'organization']} ghost>
          {/* Filtres de période */}
          <Panel 
            header={
              <Space>
                <CalendarOutlined />
                <Text strong>Période d'analyse</Text>
              </Space>
            } 
            key="period"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item label="Période prédéfinie">
                  <Select
                    aria-label="Période prédéfinie des filtres"
                    value={filters.period.preset}
                    onChange={handlePeriodPresetChange}
                    style={{ width: '100%' }}
                  >
                    {presetPeriods.map(preset => (
                      <Option key={preset.key} value={preset.key}>
                        {preset.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Période personnalisée">
                  <RangePicker
                    value={filters.period.preset === 'custom' ? [filters.period.start, filters.period.end] : undefined}
                    onChange={handleCustomPeriodChange}
                    style={{ width: '100%' }}
                    disabled={filters.period.preset !== 'custom'}
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          {/* Filtres organisationnels */}
          <Panel 
            header={
              <Space>
                <TeamOutlined />
                <Text strong>Organisation & Personnel</Text>
              </Space>
            } 
            key="organization"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Form.Item label="Départements">
                  <Select
                    aria-label="Départements des filtres"
                    mode="multiple"
                    placeholder={loadingOptions ? "Chargement..." : "Sélectionner les départements"}
                    value={filters.departments}
                    onChange={(departments) => setFilters(prev => ({ ...prev, departments }))}
                    style={{ width: '100%' }}
                    showSearch
                    loading={loadingOptions}
                    optionFilterProp="children"
                  >
                    {filterOptions.departments.map(dept => (
                      <Option key={dept.id} value={dept.id}>
                        {dept.name} ({dept.employeeCount} emp.)
                      </Option>
                    ))}
                    {filters.departments.filter(id => !filterOptions.departments.some(item => String(item.id) === String(id))).map(id =>
                      <Option key={`missing-${id}`} value={id} disabled>Sélection indisponible ({id})</Option>)}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item label="Postes">
                  <Select
                    aria-label="Postes des filtres"
                    mode="multiple"
                    placeholder={loadingOptions ? "Chargement..." : "Sélectionner les postes"}
                    value={filters.positions}
                    onChange={(positions) => setFilters(prev => ({ ...prev, positions }))}
                    style={{ width: '100%' }}
                    showSearch
                    loading={loadingOptions}
                    optionFilterProp="children"
                  >
                    {filterOptions.positions.map(pos => (
                      <Option key={pos.id} value={pos.id}>
                        {pos.title} - {pos.departmentName} ({pos.employeeCount} emp.)
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item label="Types de contrat">
                  <Select
                    aria-label="Types de contrat des filtres"
                    mode="multiple"
                    placeholder={loadingOptions ? "Chargement..." : "Sélectionner les contrats"}
                    value={filters.contractTypes}
                    onChange={(contractTypes) => setFilters(prev => ({ ...prev, contractTypes }))}
                    style={{ width: '100%' }}
                    loading={loadingOptions}
                  >
                    {filterOptions.contractTypes.map(ct => (
                      <Option key={ct.value} value={ct.value}>
                        {ct.label} ({ct.employeeCount} emp.)
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item label="Statut d'emploi">
                  <Checkbox.Group
                    options={[
                      { label: 'Employés actifs', value: 'active' },
                      { label: 'En période d\'essai', value: 'probation' },
                      { label: 'En congé', value: 'leave' },
                      { label: 'Suspendus', value: 'suspended' }
                    ]}
                    value={filters.employmentStatus}
                    onChange={(employmentStatus) => setFilters(prev => ({ ...prev, employmentStatus }))}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label="Options d'inclusion">
                  <Space direction="vertical">
                    <Switch
                      checked={filters.includeTerminated}
                      onChange={(includeTerminated) => setFilters(prev => ({ ...prev, includeTerminated }))}
                      checkedChildren="Inclus"
                      unCheckedChildren="Exclus"
                    />
                    <Text>Employés ayant quitté pendant la période</Text>
                    
                    <Switch
                      checked={filters.includeInterns}
                      onChange={(includeInterns) => setFilters(prev => ({ ...prev, includeInterns }))}
                      checkedChildren="Inclus"
                      unCheckedChildren="Exclus"
                    />
                    <Text>Stagiaires et apprentis</Text>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          {/* Filtres salariaux */}
          <Panel 
            header={
              <Space>
                <DollarOutlined />
                <Text strong>Critères Salariaux</Text>
              </Space>
            } 
            key="salary"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={16}>
                <Form.Item label={`Fourchette salariale (${filters.salaryRange[0].toLocaleString()} - ${filters.salaryRange[1].toLocaleString()} FCFA)`}>
                  <Slider
                    range
                    min={Math.min(filterOptions.salaryRange.min, filters.salaryRange[0])}
                    max={Math.max(filterOptions.salaryRange.max, filters.salaryRange[1])}
                    step={Math.max(10000, Math.floor((filterOptions.salaryRange.max - filterOptions.salaryRange.min) / 100))}
                    value={filters.salaryRange}
                    onChange={(salaryRange) => { touchedSalary.current = true; setFilters(prev => ({ ...prev, salaryRange: salaryRange as [number, number] })); }}
                    tooltip={{
                      formatter: (value) => `${value?.toLocaleString()} FCFA`
                    }}
                    disabled={loadingOptions || filterOptions.salaryRange.totalEmployees === 0}
                  />
                  {filterOptions.salaryRange.totalEmployees > 0 && (
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      Salaire moyen: {filterOptions.salaryRange.average.toLocaleString()} FCFA
                      • {filterOptions.salaryRange.totalEmployees} employés
                    </div>
                  )}
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item label="Niveau de conformité">
                  <Select
                    value={filters.complianceLevel}
                    onChange={(complianceLevel) => setFilters(prev => ({ ...prev, complianceLevel }))}
                    style={{ width: '100%' }}
                  >
                    <Option value="all">Tous les niveaux</Option>
                    <Option value="high">Élevé (&gt;= 90%)</Option>
                    <Option value="medium">Moyen (70-89%)</Option>
                    <Option value="low">Faible (&lt; 70%)</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Panel>
        </Collapse>

        {/* Filtres sauvegardés */}
        {savedFilters.length > 0 && (
          <>
            <Divider />
            <div>
              <Text strong>Filtres sauvegardés :</Text>
              <div style={{ marginTop: 8 }}>
                {savedFilters.map(filterName => (
                  <Tag 
                    key={filterName} 
                    color="blue" 
                    style={{ marginBottom: 4, cursor: 'pointer' }}
                    onClick={() => {
                      const saved = localStorage.getItem(`hr_filter_${scope.userId}_${scope.companyId}_${scope.warehouseId || ''}_${filterName}`);
                      if (saved) {
                        const parsed = JSON.parse(saved);
                        touchedSalary.current = true;
                        setFilters({ ...parsed, period: { ...parsed.period, start: parsed.period.start ? dayjs(parsed.period.start) : null,
                          end: parsed.period.end ? dayjs(parsed.period.end) : null } });
                      }
                    }}
                  >
                    {filterName}
                  </Tag>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Résumé des filtres actifs */}
        {activeFiltersCount > 0 && (
          <Alert
            message="Filtres actifs"
            description={`${activeFiltersCount} filtre${activeFiltersCount > 1 ? 's' : ''} appliqué${activeFiltersCount > 1 ? 's' : ''} à votre sélection de données.`}
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Form>
    </Card>
  );
};

export default AdvancedFilters;
